import { TFunction } from 'i18next'
import {
    DataTableColumn,
    DataTableAction,
    createOptionsFormatter,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import EditIcons from '@/icons/EditIcons'
import EyeIcons from '@/icons/EyeIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { Job } from '../../types'
import { JOB_STATUS, LOCALE_OPTIONS } from '../../constants'

/**
 * Column definitions for Jobs list
 */
export const getJobColumns = (t: TFunction): DataTableColumn<Job>[] => [
    {
        key: 'job_id',
        title: 'ID',
        width: 80,
        sortable: true,
        pinned: 'left',
    },
    {
        key: 'title',
        title: t('job-title'),
        width: 250,
        sortable: true,
        pinned: 'left',
    },
    {
        key: 'location',
        title: t('job-location'),
        width: 200,
        sortable: true,
    },
    {
        key: 'status',
        title: t('job-status'),
        width: 150,
        sortable: true,
        formatter: createOptionsFormatter({
            options: [
                { value: 'Opened', label: t('job-opened'), color: 'success' },
                { value: 'Closed', label: t('job-closed'), color: 'error' },
            ],
        }),
    },
    {
        key: 'public',
        title: t('job-public'),
        width: 130,
        sortable: true,
        formatter: createOptionsFormatter({
            options: [
                { value: true, label: 'Public', color: 'success' },
                { value: false, label: 'UnPublic', color: 'error' },
            ],
        }),
    },
    {
        key: 'locale',
        title: t('job-locale'),
        width: 120,
        sortable: true,
    },
    {
        key: 'type',
        title: t('job-type'),
        width: 150,
        sortable: true,
    },
    {
        key: 'level',
        title: t('job-level'),
        width: 200,
        sortable: true,
    },
    {
        key: 'country',
        title: t('job-country'),
        width: 130,
        sortable: true,
    },
    {
        key: 'salary',
        title: t('job-salary'),
        width: 150,
        sortable: true,
    },
]

/**
 * Filter items for Jobs list
 */
export const getJobFilters = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'status',
        type: 'select',
        label: t('job-status'),
        placeholder: t('job-status'),
        options: JOB_STATUS(t),
    },
    {
        name: 'locale',
        type: 'select',
        label: t('job-locale'),
        placeholder: t('job-locale'),
        options: LOCALE_OPTIONS,
    },
]

/**
 * Action definitions for Jobs list
 */
export const getJobActions = ({
    t,
    onEdit,
    onTogglePublic,
    onDelete,
    isPublicProcessing,
}: {
    t: TFunction
    onEdit: (row: Job) => void
    onTogglePublic: (row: Job) => void
    onDelete: (row: Job) => void
    isPublicProcessing: boolean
}): DataTableAction<Job>[] => [
    {
        key: 'edit',
        label: t('edit'),
        icon: <EditIcons className="w-4 h-4" />,
        onClick: onEdit,
        variant: 'default',
    },
    {
        key: 'toggle-public',
        label: 'Public/UnPublic',
        icon: <EyeIcons className="w-4 h-4" />,
        onClick: onTogglePublic,
        disabled: () => isPublicProcessing,
        variant: 'default',
    },
    {
        key: 'delete',
        label: t('delete'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        onClick: onDelete,
        variant: 'danger',
        className: 'text-red-600 hover:text-red-700 hover:bg-red-50',
    },
]
